'use client';
import { useState, useRef, useEffect, FC } from 'react';
import { useTranslations } from 'next-intl';
import {
  CatHeading,
  CatButton,
  CatGrid,
  CatCard,
  CatGridItem
} from 'components/blocks-components';
import Image from 'next/image';
import { replaceTokensInString } from '@cat-ecom/common/utils/stringUtils';
import { INITIAL_PAGINATION, PAGE_SIZE, PRIME_PDP_KITS } from '../../../constants/constants';
import { getProductSummary } from 'services/summary/actions';
import QuickViewDrawer from '../quick-view-drawer';
import { CurrentItemsWithPrice } from 'services/fbtItems/types';
import { CartResponse } from 'services/cart/types';
import styles from './../kit-parts-cards.module.scss';
import { useViewItemListEvent } from 'hooks/analytics/use-view-item-list-event';

interface KitCardLayoutProps {
  data: CurrentItemsWithPrice;
  isHeadlessPDPEnabled: boolean;
  locationId: string;
  cartDetails?: CartResponse;
}

const KitCardLayout: FC<KitCardLayoutProps> = ({ data, isHeadlessPDPEnabled, locationId, cartDetails }) => {
  const t = useTranslations();
  const items = data.components;
  const hasMoreItemsThanPageSize = items?.length > PAGE_SIZE;
  const [drawerActive, setDrawerActive] = useState(false);
  const [quickViewDrawerItem, setQuickViewDrawerItem] = useState({});
  const { fireEvent: fireViewItemListEvent } = useViewItemListEvent();
  const hasViewListItem = useRef(false);

  const [kitItems, setKitItems] = useState(
    hasMoreItemsThanPageSize ? items?.slice(0, PAGE_SIZE) : items
  );
  const canLoadMoreItems = kitItems?.length !== items?.length;
  const kitItemsPaginationRef = useRef(INITIAL_PAGINATION);

  const updatedProduct = items.map((item) => (
    { ...item, manufacturer: data?.manufacturer, categoryPath: data?.categoryPath }))

  const handleKitItemClick = async (partNumber, part) => {
    setDrawerActive(true);
    // TODO - Input Validation - Ensure that all values passed to the service are allowed values:
    // partsList  should be a list of strings restricting generalRestrictedCharacters (@\constants\securityConstants.ts).
    const summaryResponse = await getProductSummary([partNumber]);
    setQuickViewDrawerItem(
      summaryResponse && summaryResponse?.productCount === 1
        ? summaryResponse
        : {}
    );
  };

  useEffect(() => {
    const kitCardItemList = {
      item_list_id: PRIME_PDP_KITS,
      item_list_name: PRIME_PDP_KITS
    };
    const kitCardPayload = {
      products: updatedProduct,
      itemListData: kitCardItemList,
      cartItemsData: cartDetails,
      cartId: cartDetails?.orderHeader?.orderId,
      locationId: locationId,
      isProductsData: true
    };

    if (updatedProduct?.length > 0 && !hasViewListItem.current) {
      fireViewItemListEvent(kitCardPayload);
      hasViewListItem.current = true;
    }
  }, [cartDetails, fireViewItemListEvent, updatedProduct, locationId]);

  const handleShowMoreClick = () => {
    kitItemsPaginationRef.current++;
    setKitItems((prevItems) => {
      return [
        ...prevItems,
        ...items.slice(
          prevItems.length,
          PAGE_SIZE * kitItemsPaginationRef.current
        )
      ];
    });
  };

  const kitItemsPageResultsMsg = replaceTokensInString(
    t('MO_Page_Results', {
      '0': '{0}',
      '1': '{1}',
      '2': '{2}'
    }),
    '1',
    `${kitItems?.length}`,
    `${items?.length}`
  );

  return (
    <div className="mt-3">
      <QuickViewDrawer
        data={quickViewDrawerItem}
        drawerActive={drawerActive}
        setDrawerActive={setDrawerActive}
        isHeadlessPDPEnabled={isHeadlessPDPEnabled}
        cartDetails={cartDetails}
        locationId={locationId}
        itemListNameId={PRIME_PDP_KITS}
      />
      <CatGrid variant="3up" gap="xs" data-testid="kit-items-grid">
        {kitItems?.map((part) => {
          return (
            <CatGridItem key={part.partNumber}>
              <CatCard data-testid="kit-items-card">
                <div className="d-flex align-items-center justify-content-center cat-u-padding-sm">
                  <Image
                    src={part.thumbnail}
                    alt={`${part.partNumber}: ${part.name}`}
                    width={96}
                    height={96}
                    unoptimized
                  />
                  <div className="d-flex flex-column flex-grow-1">
                    <CatButton
                      size="md"
                      variant="ghost"
                      className={styles['kit-parts-card__btn']}
                      onClick={() => handleKitItemClick(part.partNumber, part)}
                    >
                      {`${part.partNumber}: ${part.name}`}
                    </CatButton>

                    <CatHeading variant="body-sm" className="ps-3">
                      {t('QUANTITY1')}: {part.quantity}
                    </CatHeading>
                  </div>
                </div>
              </CatCard>
            </CatGridItem>
          );
        })}
      </CatGrid>
      <div className="d-flex flex-column d-sm-flex flex-sm-row justify-content-center align-items-center mt-3">
        <CatHeading
          variant="body-sm"
          className="justify-self-center me-sm-3 mb-3 mb-sm-0"
        >
          {kitItemsPageResultsMsg}
        </CatHeading>
        {canLoadMoreItems && (
          <CatButton onClick={handleShowMoreClick}>{t('LOAD_MORE')}</CatButton>
        )}
      </div>
    </div>
  );
};

export default KitCardLayout;
